import { Trans } from "@lingui/macro"
import {
	mdiChevronDoubleRight,
	mdiEmoticonExcited,
	mdiEmoticonNeutral,
	mdiEmoticonSad,
} from "@mdi/js"
import Icon from "@mdi/react"
import {
	Box,
	Button,
	Chip,
	Grid,
	Popover,
	Typography,
} from "@mui/material"
import { lightTheme } from "commons/themes"
import TemplateSelectionModal from "components/commons/template-selection-modal"
import { ScheduleVisitModal } from "components/visit/modals"
import { NewStep } from "components/workflow/modals"
import {
	AssetMatchesBrandRejectReason,
	AssetMatchesBrandRejectReasonTranslations,
	AssetMatchesBrandStatus,
	NewStepRequest,
	PriorityLevel,
	WorkflowObjectType,
} from "models"
import React, { ReactElement } from "react"
import { UseRouterParams } from "services/router"
import Tooltip from "@mui/material/Tooltip"

interface MatchProposalItemDataProps extends UseRouterParams {
	assetId: string
	matchId: string
	logo: string
	brandName: string
	priority: PriorityLevel
	compatibility?: number
	status: AssetMatchesBrandStatus
	notes: string
	rejectReason?: AssetMatchesBrandRejectReason | null
	rejectDescription?: string | null
	createWorkflow: (templateId: string) => void
	createReminder: (request: NewStepRequest) => void
	onScheduleVisitSuccess: (message: string) => void
	onScheduleVisitError: (error: any) => void
}

interface MatchProposalItemDataState {
	openActions: boolean
	openWorkflow: boolean
	openReminder: boolean
	openScheduleVisit: boolean
}

export class MatchProposalItemData extends React.Component<MatchProposalItemDataProps, MatchProposalItemDataState> {
	private readonly actionButtonRef: React.RefObject<any> = React.createRef()

	constructor(props: MatchProposalItemDataProps) {
		super(props)
		this.state = {
			openActions: false,
			openWorkflow: false,
			openReminder: false,
			openScheduleVisit: false
		}
	}

	private getPriorityChipData = (priorityLevel: PriorityLevel): ReactElement => {
		switch (priorityLevel) {
			case PriorityLevel.URGENTE:
				return <Chip label={PriorityLevel.URGENTE} color="error" />
			case PriorityLevel.ALTA:
				return <Chip label={PriorityLevel.ALTA} color="error" />
			case PriorityLevel.NORMALE:
				return <Chip label={PriorityLevel.NORMALE} sx={{ backgroundColor: "#ef832e", color: "white" }} />
			case PriorityLevel.BASSA:
				return <Chip label={PriorityLevel.BASSA} sx={{ backgroundColor: "#fef022" }} />
		}
	}
	private getStatusChipData = (status: AssetMatchesBrandStatus): ReactElement => {
		switch (status) {
			case AssetMatchesBrandStatus.CREATED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.CREATED"}>
								Creato
							</Trans>
						}
						sx={{ backgroundColor: "#adb5bd", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.APPROVED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.APPROVED"}>
								Approvato
							</Trans>
						}
						sx={{ backgroundColor: "success.light", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.INSPECTION:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.INSPECTION"}>
								Sopralluogo
							</Trans>
						}
						sx={{ backgroundColor: "primary.main", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.INTERESTED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.INTERESTED"}>
								Interessato
							</Trans>
						}
						sx={{ backgroundColor: "secondary.main", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.SENT:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.SENT"}>
								Inviato
							</Trans>
						}
						sx={{ backgroundColor: "#ef832e", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.REJECTED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.REJECTED"}>
								Rifiutato
							</Trans>
						}
						sx={{ backgroundColor: "#000000", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.CONTRACT_DONE:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.CONTRACT_DONE"}>
								Contrattualizzata
							</Trans>
						}
						sx={{ backgroundColor: "success.light", textTransform: "uppercase", color: "white" }}
					/>
				)
		}
	}
	private getRejectTooltipContent = (): string => {
		const parts: string[] = []
		const { rejectReason, rejectDescription } = this.props

		if (rejectReason) {
			const translations = new AssetMatchesBrandRejectReasonTranslations()
			const reasonKey = rejectReason as keyof AssetMatchesBrandRejectReasonTranslations
			const translatedReason = translations[reasonKey]
			if (translatedReason) {
				parts.push(translatedReason)
			}
		}

		if (rejectDescription && rejectDescription.trim().length > 0) {
			parts.push(rejectDescription.trim())
		}

		return parts.join("\n")
	}
	private ellipsis = (str: string, num: number = str.length, ellipsisStr = "...") =>
		str.length >= num
			? str.slice(0, num >= ellipsisStr.length ? num - ellipsisStr.length : num) +
			ellipsisStr
			: str;

	private getCompatibility = (compatibility: number): ReactElement => {

		let emoticon: ReactElement = (
			<Box sx={{ display: "flex", gap: 2 }}>
				<Icon path={mdiEmoticonExcited} size={1} color={lightTheme.palette.info.main} />
				<Icon path={mdiEmoticonNeutral} size={1} style={{ color: "#adb5bd" }} />
				<Icon path={mdiEmoticonSad} size={1} style={{ color: "#adb5bd" }} />
			</Box>
		)

		if (compatibility <= 66) {
			emoticon = (
				<Box sx={{ display: "flex", gap: 2 }}>
					<Icon path={mdiEmoticonExcited} size={1} style={{ color: "#adb5bd" }} />
					<Icon path={mdiEmoticonNeutral} size={1} color={lightTheme.palette.info.main} />
					<Icon path={mdiEmoticonSad} size={1} style={{ color: "#adb5bd" }} />
				</Box>
			)
		}

		if (compatibility <= 33) {
			emoticon = (
				<Box sx={{ display: "flex", gap: 2 }}>
					<Icon path={mdiEmoticonExcited} size={1} style={{ color: "#adb5bd" }} />
					<Icon path={mdiEmoticonNeutral} size={1} style={{ color: "#adb5bd" }} />
					<Icon path={mdiEmoticonSad} size={1} color={lightTheme.palette.info.main} />
				</Box>
			)
		}

		return emoticon
	}

	render() {
		return (
			<>
				<Grid item md={1}>
					<img src={this.props.logo} alt="logo" width={50} style={{ borderRadius: 30 }} />
				</Grid>
				<Grid item md={2}>
					<Typography fontWeight="bold" fontSize="0.8rem">
						{this.props.brandName}
					</Typography>
				</Grid>
				<Grid item md={1}>
					<Typography fontWeight="bold" fontSize="0.8rem">
						{this.getPriorityChipData(this.props.priority)}
					</Typography>
				</Grid>
				<Grid item md={1.5}>
					<Typography fontWeight="bold" fontSize="0.8rem">
						{this.props.status === AssetMatchesBrandStatus.REJECTED && (this.props.rejectReason || this.props.rejectDescription) ? (
							<Tooltip
								arrow
								placement="top"
								title={<Box sx={{ whiteSpace: "pre-line" }}>{this.getRejectTooltipContent()}</Box>}
							>
								<Box component="span">
									{this.getStatusChipData(this.props.status)}
								</Box>
							</Tooltip>
						) : (
							this.getStatusChipData(this.props.status)
						)}
					</Typography>
				</Grid>
				<Grid item md={2}>
					{this.getCompatibility(this.props.compatibility || 0)}
				</Grid>
				<Grid item md={2}>
					<Typography fontWeight="bold" fontSize="0.8rem">
						{this.props.notes && this.props.notes !== "" ? this.ellipsis(this.props.notes, 30) : "-"}
					</Typography>
				</Grid>
				<Grid md={2.5}>
					<Button
						variant={"outlined"}
						color={"info"}
						startIcon={<Icon path={mdiChevronDoubleRight}
							size={1}
							color={lightTheme.palette.info.main} />}
						sx={{
							px: 1,
							fontSize: "0.8rem"
						}}
						ref={this.actionButtonRef}
						onClick={() => this.setState(() => ({ openActions: true }))}
					>
						<Trans id={"dms-fast-actions"}>Azioni rapide</Trans>
					</Button>
					<Button
						variant={"contained"}
						color={"info"}
						sx={{
							px: 1,
							ml: 2,
							fontSize: "0.8rem"
						}}
						onClick={() => this.props.navigate(`/match-proposal/match/${this.props.matchId}`)}
					>
						<Trans id={"dms-redirect-match-page"}>Vedi proposta</Trans>
					</Button>
				</Grid>

				<Popover
					anchorEl={this.actionButtonRef.current}
					open={this.state.openActions}
					onClose={() => this.setState(() => ({ openActions: false }))}
					anchorOrigin={{
						vertical: "center",
						horizontal: "left",
					}}
					transformOrigin={{
						vertical: "center",
						horizontal: "right",
					}}
					slotProps={{
						paper: {
							sx: {
								p: 2,
								overflow: "hidden",
								width: "200px",
							},
						},
					}}
				>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "start",
							gap: 2,
						}}
					>
						<Box
							sx={{
								cursor: "pointer",
							}}
							onClick={() => this.props.navigate(`/match-proposal/asset/${this.props.assetId}`)}
						>
							<Trans id={"dms-fast-actions.sent-match"}>Invia proposta</Trans>
						</Box>
						<Box
							sx={{
								cursor: "pointer",
							}}
							onClick={() => this.setState(() => ({
								openReminder: true,
								openActions: false,
							}))}
						>
							<Trans id={"dms-fast-actions.reminder"}>Crea promemoria</Trans>
						</Box>
						<Box
							sx={{
								cursor: "pointer",
							}}
							onClick={() => this.setState(() => ({
								openWorkflow: true,
								openActions: false,
							}))}
						>
							<Trans id={"dms-fast-actions.workflow"}>Scatena workflow</Trans>
						</Box>
						<Box
							sx={{
								cursor: "pointer",
							}}
							onClick={() => this.setState(() => ({
								openScheduleVisit: true,
								openActions: false,
							}))}
						>
							<Trans id={"dms-fast-actions.schedule-visit"}>Programma sopralluogo</Trans>
						</Box>
					</Box>
				</Popover>
				{this.state.openWorkflow && <TemplateSelectionModal
					templateType={WorkflowObjectType.MATCH}
					onClose={() => this.setState(() => ({ openWorkflow: false }))}
					submit={(templateId: string) => {
						this.setState(() => ({ openWorkflow: false }))
						this.props.createWorkflow(templateId)
					}}
				/>}
				{this.state.openReminder && <NewStep
					openDialog
					onClose={() => this.setState(() => ({ openReminder: false }))}
					submit={(request: NewStepRequest) => {
						this.setState(() => ({ openReminder: false }))
						this.props.createReminder(request)
					}}
					action
					create
				/>}
				{this.state.openScheduleVisit && <ScheduleVisitModal
					open={this.state.openScheduleVisit}
					onClose={() => this.setState({ openScheduleVisit: false })}
					assetId={this.props.assetId}
					matchId={this.props.matchId}
					onSuccess={this.props.onScheduleVisitSuccess}
					onError={this.props.onScheduleVisitError}
				/>}
			</>
		)
	}
}
